/**
 * WHICH BRAIN IS ANSWERING — one derivation, two readers (#1466 F-30C, ruling 6).
 *
 * The console used to render `anthropic · claude-opus-4-8` in a monospace span
 * fixed to the bottom of EVERY page. That is a provider slug and a model id, in
 * the permanent chrome, in front of a customer-facing deployment's staff — the
 * exact thing ruling 6 removes: *"Move provider/model identity to Settings > AI
 * diagnostics. No raw model slug in the permanent footer."*
 *
 * The badge was not useless, and this is why the identity moves rather than
 * being deleted. `core.setting` may declare `ai.model` and `ai.provider`, and it
 * may declare NEITHER — in which case the copilot still runs, on the fallbacks
 * below. So "what is configured" and "what will actually answer you" are two
 * different questions, and only the second one is a diagnostic. Settings shows
 * the configured value in an editable field; this is what tells an administrator
 * whether that field is doing anything.
 *
 * Derived rather than restated (rule 42): the fallbacks exist once, and the bar
 * that runs the request and the panel that describes it read the same function.
 * Two copies would differ the day somebody changed a default, and the copy
 * nobody looks at would be the one on the screen.
 */
import type { SettingRow } from '@solum/sdk'

/**
 * What answers when `core.setting` declares nothing. These are the values
 * `CopilotBar` has always passed to `runCopilot`; they are stated here so the
 * request and the diagnostic cannot disagree about them.
 */
export const AI_FALLBACK = { provider: 'anthropic', model: 'claude-opus-4-8' } as const

export interface AiIdentity {
  provider: string
  model: string
  /** True when BOTH come from `core.setting` rather than from `AI_FALLBACK`. */
  configured: boolean
  /** True when exactly one half is configured and the other is a fallback. */
  partial: boolean
}

/**
 * The identity a copilot request will actually carry, from a `describeSettings`
 * answer.
 *
 * `rows` may be undefined (the read has not settled, or was refused). That is
 * reported as unconfigured rather than as an error: the fallbacks are what the
 * request would use either way, so the sentence "this is what will answer you"
 * stays true, and a settings surface that cannot read settings has a louder
 * problem than this line.
 */
export function aiIdentity(rows: readonly SettingRow[] | undefined): AiIdentity {
  const valueOf = (key: string) => {
    const found = rows?.find((row) => row.key === key)?.value
    return found === undefined || found === null || found === '' ? null : String(found)
  }
  const provider = valueOf('provider')
  const model = valueOf('model')
  return {
    provider: provider ?? AI_FALLBACK.provider,
    model: model ?? AI_FALLBACK.model,
    configured: provider !== null && model !== null,
    partial: (provider !== null) !== (model !== null),
  }
}
